import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { UserState } from './UserState';
import { UserResponse } from '../client';
import { useApi } from './ApiContext';
import { rememberLastUsedLogin } from '../auth/last-used-login';

interface UserContextType {
  userState: UserState;
  user: UserResponse | null;
  isLoggedIn: boolean;
  setUser: (user: UserResponse | null) => void;
  setLoggedIn: (status: boolean) => void;
  logout: () => void;
  fetchUserData: () => Promise<UserResponse | null>;
  getAuthGeneration: () => number;
}

const initialUserState = new UserState();

/** True when two `/api/user/me` reads describe the same account, field for field. */
function sameUser(
  current: UserResponse | null,
  next: UserResponse | null,
): boolean {
  if (current === next) return true;
  if (!current || !next) return false;
  return (
    current.id === next.id &&
    current.email === next.email &&
    current.firstName === next.firstName &&
    current.lastName === next.lastName &&
    (current.loginMethod ?? null) === (next.loginMethod ?? null)
  );
}

const UserContext = createContext<UserContextType>({
  userState: initialUserState,
  user: initialUserState.user,
  isLoggedIn: initialUserState.isLoggedIn,
  setUser: () => {},
  setLoggedIn: () => {},
  logout: () => {},
  fetchUserData: async () => null,
  getAuthGeneration: () => 0,
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userState] = useState(new UserState());
  const [user, setRenderedUser] = useState<UserResponse | null>(userState.user);
  const [isLoggedIn, setRenderedLoggedIn] = useState(userState.isLoggedIn);
  const authGeneration = useRef(0);
  const { userApi } = useApi();

  const getAuthGeneration = useCallback(() => authGeneration.current, []);

  const setUser = useCallback(
    (nextUser: UserResponse | null) => {
      const wasLoggedIn = userState.isLoggedIn;
      const previousUserId = userState.user?.id;
      const nextLoggedIn = nextUser !== null;
      // The provider, the header and the route guard each read
      // `/api/user/me` on load, so the same account arrives two or three
      // times. Re-publishing an identical account would hand every consumer
      // a fresh object and re-run their `[user]` effects, which is how a
      // redundant refetch ends up resetting form state a person is typing
      // into. Nothing has changed, so nothing is published.
      if (
        wasLoggedIn === nextLoggedIn &&
        sameUser(userState.user, nextUser) &&
        userState.user !== null
      ) {
        return;
      }
      if (
        wasLoggedIn !== nextLoggedIn ||
        (wasLoggedIn && nextLoggedIn && previousUserId !== nextUser?.id)
      ) {
        authGeneration.current += 1;
      }
      userState.setUser(nextUser);
      userState.setLoggedIn(nextLoggedIn);
      setRenderedUser(nextUser);
      setRenderedLoggedIn(nextLoggedIn);
    },
    [userState],
  );

  const setLoggedIn = useCallback(
    (status: boolean) => {
      if (userState.isLoggedIn !== status) authGeneration.current += 1;
      userState.setLoggedIn(status);
      setRenderedLoggedIn(status);
      setRenderedUser(userState.user);
    },
    [userState],
  );

  const logout = useCallback(() => {
    // This fence must advance before React renders or effect cleanup runs.
    authGeneration.current += 1;
    userState.logout();
    setRenderedLoggedIn(false);
    setRenderedUser(null);
  }, [userState]);

  // Fetch user data from API on initial load
  useEffect(() => {
    const fetchInitialUserData = async () => {
      try {
        const response = await userApi.apiUserMeGet();
        if (response.status === 200 && response.data) {
          if (response.data.loginMethod) {
            rememberLastUsedLogin(response.data.loginMethod);
          }
          setUser(response.data);
        }
      } catch (error) {
        // If error or no user data, ensure logged out state
        console.log('No active user session or error fetching user data');
      }
    };

    fetchInitialUserData();
  }, [setUser, userApi]);

  // Function to fetch user data from API
  const fetchUserData = useCallback(async (): Promise<UserResponse | null> => {
    try {
      const response = await userApi.apiUserMeGet();
      if (response.status === 200 && response.data) {
        if (response.data.loginMethod) {
          rememberLastUsedLogin(response.data.loginMethod);
        }
        setUser(response.data);
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }, [setUser, userApi]);

  return (
    <UserContext.Provider
      value={{
        userState,
        user,
        isLoggedIn,
        setUser,
        setLoggedIn,
        logout,
        fetchUserData,
        getAuthGeneration,
      }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
